import React, {
    useState,
    useImperativeHandle,
    forwardRef,
    useEffect,
} from 'react';
import s from './s.module.less';
import { Button, Descriptions, Form, Input } from 'antd';
import type { TAssessmentItem } from 'types/operation';
import ConditionSelect from 'components/form/ConditionSelect';
import FileUpload from 'components/form/FileUpload';
import commonS from 'styles/common.module.less';
import useLoadBasicData from 'hooks/useLoadBasicData';
import { isDevEnv, isTestEnv, isProductionEnv, isPrereleaseEnv } from 'utils/common';
import ExternalLinkIcon from 'assets/common/externalLinkIcon.svg';

type IProps = {
    item: TAssessmentItem;
    onSuccess: (obj: Partial<TAssessmentItem>) => void;
};

export interface IntroCardA {
    validateAndSubmit: () => boolean;
}

const IntroCard = forwardRef<IntroCardA, IProps>((props, ref) => {
    const { item, onSuccess } = props;
    const [isEdit, setIsEdit] = useState(false);
    const [form] = Form.useForm<TAssessmentItem & { active: boolean }>();
    const [loadBaiscData] = useLoadBasicData();

    const handleEdit = () => {
        setIsEdit(true);
    };

    const handleCancel = () => {
        setIsEdit(false);
    };

    const handleSave = async () => {
        const formValues = await form.validateFields();
        onSuccess({
            assessmentName: formValues.assessmentName,
            conditionKeyTags: (formValues.tags || []).join(','),
            partnerName: formValues.partnerName,
            iconUrl: formValues.iconUrl || '',
        });
        setIsEdit(false);
    };

    const getLinkUrl = (): string => {
        if (!item?.uuid) {
            return '';
        }
        if (isDevEnv()) {
            return `https://dev.d2ysjydukc4efk.amplifyapp.com/awsome-embed?key=AK-test-api-key-1&uuid=${item.uuid}`;
        }
        if (isTestEnv()) {
            return `https://test.d2ysjydukc4efk.amplifyapp.com/awsome-embed?key=AK-test-api-key-1&uuid=${item.uuid}`;
        }
        if (isProductionEnv() || isPrereleaseEnv()) {
            return `https://www.helloklarity.com/awsome-embed?key=sk_prod_mh5dfc2mKXpI&uuid=${item.uuid}`;
        }
        return '';
    };

    const renderDescription = () => {
        return (
            <Descriptions
                colon={false}
                title="Workflow information"
                layout="vertical"
                extra={<Button onClick={() => handleEdit()}>Edit</Button>}
                column={1}
                size="small"
                contentStyle={{
                    marginBottom: '16px',
                }}
            >
                <Descriptions.Item label="Workflow name">
                    {item.assessmentName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Condition">
                    {item.conditionKeyTags || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Partner name">
                    <div>{item.partnerName || '-'}</div>
                </Descriptions.Item>
                <Descriptions.Item label="Icon">
                    {item.iconUrl ? (
                        <img src={item.iconUrl} alt="icon" className={s.iconImage} />
                    ) : (
                        <div>-</div>
                    )}
                </Descriptions.Item>
                <Descriptions.Item label="Link">
                    {getLinkUrl() ? (
                        <a
                            href={getLinkUrl()}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                marginTop: 4,
                            }}
                        >
                            {getLinkUrl()}
                        </a>
                    ) : (
                        '-'
                    )}
                </Descriptions.Item>
            </Descriptions>
        );
    };

    const renderForm = () => {
        return (
            <div>
                <div className={s.headerBar}>
                    <h4 className={s.subtitle}>Workflow information</h4>
                    <div className={s.buttonWrap}>
                        <Button onClick={() => handleCancel()}>Cancel</Button>
                        <Button
                            onClick={() => handleSave()}
                            type="primary"
                            className={s.ml8}
                        >
                            Save changes
                        </Button>
                    </div>
                </div>
                <p className={s.tips}>
                    <span>* </span>
                    Indicates required fields
                </p>
                <Form
                    form={form}
                    className={commonS.formStyle1}
                    initialValues={{ active: true }}
                    autoComplete="off"
                    layout="vertical"
                    scrollToFirstError
                >
                    <>
                        <Form.Item
                            label="Assessment name"
                            name="assessmentName"
                            validateFirst
                            rules={[
                                {
                                    required: true,
                                    message: 'Assessment name is required',
                                },
                            ]}
                        >
                            <Input
                                maxLength={70}
                                showCount
                                placeholder="Ex. ADHD self assessment"
                            />
                        </Form.Item>
                        <Form.Item
                            className={s.rowItem}
                            label="Conditions"
                            name="tags"
                            rules={[
                                {
                                    required: true,
                                    message: 'At least one condition is required',
                                },
                            ]}
                        >
                            <ConditionSelect isLabel mode="multiple" />
                        </Form.Item>
                        <Form.Item
                            className={s.rowItem}
                            label="Partner name"
                            name="partnerName"
                        >
                            <Input maxLength={70} showCount />
                        </Form.Item>
                        <Form.Item 
                            label="Icon" 
                            name="iconUrl"
                            valuePropName="value"
                            getValueFromEvent={(url) => url}
                        >
                            <FileUpload
                                accept="image/*"
                                acceptTypeStr="PNG, JPG, GIF"
                                maxSize={10}
                                showImage={true}
                            />
                        </Form.Item>
                    </>
                </Form>
            </div>
        );
    };

    useImperativeHandle(ref, () => ({
        validateAndSubmit: () => {
            return !isEdit;
        },
    }));

    useEffect(() => {
        if (item) {
            form.setFieldsValue({
                assessmentName: item.assessmentName,
                tags: item.conditionKeyTags?.split(','),
                partnerName: item.partnerName,
                iconUrl: item.iconUrl || '',
            });
        } else {
            form.resetFields();
        }
    }, [item]);

    useEffect(() => {
        loadBaiscData();
    }, [loadBaiscData]);

    return (
        <div className={s.wrap}>
            <div className={s.content}>
                {isEdit ? renderForm() : renderDescription()}
            </div>
        </div>
    );
});

export default IntroCard;
